<template>
  <v-container fluid class="pa-0">
    <!-- Aikavälisuodatin -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-cash-multiple</v-icon>
        {{ $t('sites.billing_proposal.title') }}
        <v-spacer />
        <v-btn
          v-if="$isTenantAdmin"
          :loading="downloadingExcel"
          color="primary"
          @click="downloadExcel"
        >
          <v-icon class="no-gradient" left>mdi-download-outline</v-icon>
          {{ $t('sites.billing_proposal.download_excel') }}
        </v-btn>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatIsoDateFi(startDate)"
                  :label="$t('sites.billing_proposal.start_date')"
                  prepend-icon="mdi-calendar"
                  readonly clearable v-bind="attrs" v-on="on" outlined dense
                  @click:clear="startDate = null"
                />
              </template>
              <v-date-picker v-model="startDate" locale="fi" @input="startDateMenu = false" />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatIsoDateFi(endDate)"
                  :label="$t('sites.billing_proposal.end_date')"
                  prepend-icon="mdi-calendar"
                  readonly clearable v-bind="attrs" v-on="on" outlined dense
                  @click:clear="endDate = null"
                />
              </template>
              <v-date-picker v-model="endDate" locale="fi" @input="endDateMenu = false" />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Lisätyöt ja odotukset tuntikirjauksista -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-account-clock</v-icon>
        {{ $t('sites.billing_proposal.extra_work_title') }}
      </v-card-title>
      <v-card-text class="pa-0">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('sites.billing_proposal.date') }}</th>
                <th class="text-left">{{ $t('sites.billing_proposal.time') }}</th>
                <th class="text-right">{{ $t('sites.billing_proposal.duration') }}</th>
                <th class="text-left"></th>
                <th class="text-left">{{ $t('sites.billing_proposal.worker') }}</th>
                <th class="text-left">{{ $t('sites.billing_proposal.work_type') }}</th>
                <th class="text-left">{{ $t('sites.billing_proposal.work') }}</th>
                <th class="text-left">{{ $t('sites.billing_proposal.info') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in extraWorkEntries" :key="entry.id">
                <td>{{ $formatDateNoTime(entry.entryStart) }}</td>
                <td>{{ $formatTimeNoDate(entry.entryStart) }}-{{ $formatTimeNoDate(entry.entryEnd) }}</td>
                <td class="text-right">{{ $formatNumber(entry.hours, '0.00') }} h</td>
                <td>
                  <v-chip v-if="entry.chargeType === 'TUNTITYO'" x-small color="warning">Lisätyö</v-chip>
                  <v-chip v-else-if="entry.chargeType === 'ODOTUS'" x-small color="error">Odotus</v-chip>
                </td>
                <td>{{ entry.workerName || '-' }}</td>
                <td>{{ entry.workTypeName || '-' }}</td>
                <td>{{ entry.workTitle || '-' }}</td>
                <td>{{ entry.info || '-' }}</td>
              </tr>
              <tr v-if="extraWorkEntries.length === 0 && !loading">
                <td colspan="8" class="text-center grey--text py-4">
                  {{ $t('sites.billing_proposal.no_extra_work') }}
                </td>
              </tr>
              <tr v-for="total in extraWorkTotals" :key="total.chargeType" class="font-weight-bold">
                <td colspan="2" class="text-right">
                  {{ total.chargeType === 'TUNTITYO' ? 'Lisätyö' : 'Odotus' }}
                  {{ $t('sites.billing_proposal.total').toLowerCase() }}
                </td>
                <td class="text-right">{{ $formatNumber(total.hours, '0.00') }} h</td>
                <td colspan="5"></td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-progress-linear v-if="loading" indeterminate color="primary" />
      </v-card-text>
    </v-card>

    <!-- Suoritemäärät päiväkirjamerkinnöistä -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-arrow-expand-horizontal</v-icon>
        {{ $t('sites.billing_proposal.quantities_title') }}
      </v-card-title>
      <v-card-text class="pa-0">
        <div v-if="quantityGroups.length === 0 && !loading" class="text-center grey--text py-4">
          {{ $t('sites.billing_proposal.no_quantities') }}
        </div>
        <template v-for="group in quantityGroups">
          <v-subheader :key="`header-${group.key}`" class="font-weight-bold">
            {{ group.workTitle || '-' }}
          </v-subheader>
          <v-simple-table :key="`table-${group.key}`" dense class="mb-2">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">{{ $t('sites.billing_proposal.date') }}</th>
                  <th class="text-left">{{ $t('sites.billing_proposal.quantity_label') }}</th>
                  <th class="text-right">{{ $t('sites.billing_proposal.quantity_value') }}</th>
                  <th class="text-right">{{ $t('sites.billing_proposal.cumulative_total') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="day in group.days">
                  <tr
                    v-for="(row, ri) in day.rows"
                    :key="`${group.key}-${day.key}-${ri}`"
                    :class="{ 'day-group-start': ri === 0 }"
                  >
                    <td>{{ ri === 0 ? $formatDateNoTime(day.date) : '' }}</td>
                    <td>{{ row.label }}</td>
                    <td class="text-right">{{ $formatNumber(row.value, '0.00') }} {{ row.unit }}</td>
                    <td class="text-right">
                      <template v-if="row.cumulative">{{ $formatNumber(row.cumulativeTotal, '0.00') }} {{ row.unit }}</template>
                      <template v-else>-</template>
                    </td>
                  </tr>
                </template>
                <tr v-for="total in group.totals" :key="`${group.key}-total-${total.attributeKey}`" class="font-weight-bold">
                  <td></td>
                  <td>{{ total.label }} {{ $t('sites.billing_proposal.total').toLowerCase() }}</td>
                  <td class="text-right">{{ $formatNumber(total.total, '0.00') }} {{ total.unit }}</td>
                  <td></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>
      </v-card-text>
    </v-card>

    <!-- Työmaalle toimitetut materiaalit -->
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left>mdi-truck-delivery</v-icon>
        {{ $t('sites.billing_proposal.materials_title') }}
      </v-card-title>
      <v-card-text class="pa-0">
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('sites.billing_proposal.date') }}</th>
                <th class="text-left">{{ $t('sites.billing_proposal.dispatch_number') }}</th>
                <th class="text-left">{{ $t('sites.billing_proposal.material') }}</th>
                <th class="text-right">{{ $t('sites.billing_proposal.quantity_value') }}</th>
                <th class="text-right">{{ $t('sites.billing_proposal.material_cost') }}</th>
                <th class="text-left"></th>
              </tr>
            </thead>
            <tbody v-for="note in filteredDispatchNotes" :key="note.id">
              <tr v-for="(item, ii) in note.items" :key="`${note.id}-${ii}`">
                <td>{{ ii === 0 ? $formatDateNoTime(note.createdAt) : '' }}</td>
                <td>{{ ii === 0 ? note.dispatchNumber : '' }}</td>
                <td>
                  <span v-if="item.item && item.item.typeName" class="grey--text text--darken-1">{{ item.item.typeName }} </span>{{ item.item ? item.item.name : '-' }}
                </td>
                <td class="text-right">
                  <template v-if="item.unitQuantity">
                    {{ $formatNumber(item.pieces, '0,0') }} kpl / {{ $formatNumber(item.totalQuantity, '0,0.[00]') }} {{ (item.item && item.item.unit) || 'm' }}
                  </template>
                  <template v-else>
                    {{ $formatNumber(item.totalQuantity, '0,0.[00]') }} {{ (item.item && item.item.unit) || 'kpl' }}
                  </template>
                </td>
                <td class="text-right">{{ formatEuro(materialCost(item)) }}</td>
                <td>
                  <v-chip v-if="ii === 0 && note.status !== 'COMPLETED'" x-small color="grey" dark>
                    {{ $t('inventory.statuses.' + note.status) }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
            <tbody v-if="filteredDispatchNotes.length > 0">
              <tr class="font-weight-bold">
                <td colspan="4">{{ $t('sites.billing_proposal.material_cost_total') }}</td>
                <td class="text-right">{{ formatEuro(materialCostTotal) }}</td>
                <td></td>
              </tr>
            </tbody>
            <tbody v-if="filteredDispatchNotes.length === 0 && !loadingDispatchNotes">
              <tr>
                <td colspan="6" class="text-center grey--text py-4">
                  {{ $t('sites.billing_proposal.no_materials') }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-progress-linear v-if="loadingDispatchNotes" indeterminate color="primary" />
      </v-card-text>
    </v-card>

    <!-- Ostolaskut: olemassa oleva listaus sellaisenaan -->
    <site-netvisor-invoices :site="site" />
  </v-container>
</template>

<script>
import dayjs from 'dayjs';
import billingProposalApi from '@/api/billingProposal';
import inventoryApi from '@/api/inventory';
import filenameValidator from '@/plugins/filenamevalidator.plugin';
import downloadjs from 'downloadjs';
import SiteNetvisorInvoices from '@/components/Sites/SiteNetvisorInvoices';

export default {
  name: 'SiteBillingProposal',
  components: { SiteNetvisorInvoices },
  props: {
    site: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      proposal: null,
      dispatchNotes: [],
      loading: false,
      loadingDispatchNotes: false,
      downloadingExcel: false,
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
    };
  },
  computed: {
    extraWorkEntries() {
      return (this.proposal && this.proposal.extraWorkEntries) || [];
    },
    extraWorkTotals() {
      const totals = [];
      for (const chargeType of ['TUNTITYO', 'ODOTUS']) {
        const entries = this.extraWorkEntries.filter(e => e.chargeType === chargeType);
        if (entries.length) {
          totals.push({ chargeType, hours: entries.reduce((sum, e) => sum + (e.hours || 0), 0) });
        }
      }
      return totals;
    },
    // Suoriterivit ja -summat ryhmiteltynä työn ja sen alla päivän mukaan
    quantityGroups() {
      if (!this.proposal) return [];
      const groups = new Map();
      for (const row of this.proposal.quantityRows || []) {
        const key = String(row.workId);
        if (!groups.has(key)) {
          groups.set(key, { key, workTitle: row.workTitle, days: new Map(), totals: [] });
        }
        // Yhden päivän suoritteet samaan ryhmään luettavuuden parantamiseksi
        const days = groups.get(key).days;
        const dayKey = String(row.daybookId);
        if (!days.has(dayKey)) {
          days.set(dayKey, { key: dayKey, date: row.daybookDate, rows: [] });
        }
        days.get(dayKey).rows.push(row);
      }
      for (const total of this.proposal.quantityTotals || []) {
        const group = groups.get(String(total.workId));
        if (group) group.totals.push(total);
      }
      return Array.from(groups.values()).map(g => ({
        key: g.key,
        workTitle: g.workTitle,
        days: Array.from(g.days.values()),
        totals: g.totals,
      }));
    },
    materialCostTotal() {
      return this.filteredDispatchNotes
        .flatMap(n => n.items)
        .reduce((sum, item) => sum + (this.materialCost(item) || 0), 0);
    },
    filteredDispatchNotes() {
      return this.dispatchNotes
        .filter(n => n.direction === 'TO_SITE')
        .filter(n => this.inRange(n.createdAt));
    },
    datesParam() {
      if (!this.startDate && !this.endDate) return undefined;
      const start = this.startDate ? dayjs(this.startDate).startOf('day') : dayjs('1970-01-01');
      const dates = [start.toISOString()];
      if (this.endDate) dates.push(dayjs(this.endDate).endOf('day').toISOString());
      return dates;
    },
  },
  watch: {
    'site.id'() {
      this.loadAll();
    },
    datesParam() {
      this.loadProposal();
    },
  },
  methods: {
    materialCost(item) {
      const costs = this.proposal && this.proposal.materialCosts;
      return costs && costs[item.id] !== undefined ? costs[item.id] : null;
    },
    formatEuro(value) {
      if (value === null || value === undefined) return '';
      return Number(value).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
    },
    async loadAll() {
      await Promise.all([this.loadProposal(), this.loadDispatchNotes()]);
    },
    async loadProposal() {
      if (!this.site || !this.site.id) return;
      this.loading = true;
      try {
        this.proposal = await billingProposalApi.getSiteBillingProposal(this.site.id, this.datesParam);
      } catch (error) {
        console.error('Error loading billing proposal:', error);
        this.showError(this.$t('sites.billing_proposal.load_failed'));
      } finally {
        this.loading = false;
      }
    },
    async loadDispatchNotes() {
      if (!this.site || !this.site.id) return;
      this.loadingDispatchNotes = true;
      try {
        this.dispatchNotes = await inventoryApi.getSiteDispatchNotes(this.site.id);
      } catch (error) {
        console.error('Error loading dispatch notes:', error);
        this.showError(this.$t('sites.billing_proposal.load_failed'));
      } finally {
        this.loadingDispatchNotes = false;
      }
    },
    async downloadExcel() {
      if (!this.site || !this.site.id) return;
      this.downloadingExcel = true;
      try {
        const blob = await billingProposalApi.getSiteBillingProposalXlsx(this.site.id, this.datesParam);
        const range = [this.startDate, this.endDate]
          .filter(Boolean)
          .map(d => dayjs(d).format('YYYYMMDD'))
          .join('-');
        // Projektikoodi + työmaan nimi eteen, jotta ladatut tiedostot lajittuvat koodin mukaan.
        const projectCode = this.site.project ? this.site.project.code : '';
        const siteLabel = [projectCode, this.site.name].filter(Boolean).join(' ');
        downloadjs(
          blob,
          filenameValidator(
            [siteLabel, 'Laskutusehdotus', range]
              .filter(Boolean)
              .join('_')
              .replace(/ /g, '-')
              .concat('.xlsx')
          )
        );
      } catch (error) {
        console.error('Error downloading billing proposal excel:', error);
        this.showError(this.$t('sites.billing_proposal.load_failed'));
      } finally {
        this.downloadingExcel = false;
      }
    },
    inRange(date) {
      if (!date) return true;
      if (this.startDate && dayjs(date).isBefore(dayjs(this.startDate).startOf('day'))) return false;
      if (this.endDate && dayjs(date).isAfter(dayjs(this.endDate).endOf('day'))) return false;
      return true;
    },
    formatIsoDateFi(date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '';
    },
    showError(message) {
      this.$store.dispatch('showNotification', { color: 'error', message, timeOut: 3000 });
    },
  },
  mounted() {
    this.loadAll();
  },
};
</script>

<style scoped>
/* Erottele yhden päivän suoriterivit edellisestä päivästä */
.day-group-start td {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}
</style>
